import * as React from 'react';
import styles from './PlaceholderReadWriteRecipientWell.scss';
import { IFocusable } from '../types/IFocusable';
import { VarianceHack } from '../types/VarianceHack';
import { useInjected } from 'owa-inject';
import updateQueryString from 'owa-readwrite-recipient-well-fabric/lib/actions/updateQueryString';
import FindControlViewState from 'owa-recipient-common/lib/store/schema/FindControlViewState';

export interface PlaceholderReadWriteRecipientWellProps {
    viewState: FindControlViewState;
    onImportTriggered: () => void;
    recipientNames: (string | null)[];
    onFocus?: () => void;
    onBlur?: () => void;
    wellLabel: React.ReactElement;
}

/**
 * Simple mock well with static pills that collects input to the viewState while the proper
 * recipient well loads.
 */
export const PlaceholderReadWriteRecipientWell = React.forwardRef(
    (
        {
            viewState,
            onImportTriggered,
            recipientNames,
            onFocus,
            onBlur,
            wellLabel,
        }: PlaceholderReadWriteRecipientWellProps,
        ref: React.Ref<IFocusable>
    ) => {
        const [updateQueryStringInjected] = useInjected(updateQueryString);

        const onChange = React.useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                // update the querystring on the viewState so it will be
                // set when we switch to the real recipient well.
                updateQueryStringInjected(viewState, e.target.value);
                // Trigger interaction listener in parent
                onImportTriggered();
            },
            [onImportTriggered, updateQueryStringInjected, viewState]
        );

        const pills = React.useMemo(
            () =>
                recipientNames.map((recipientName: string | null, index: number) => (
                    <div
                        key={recipientName || index}
                        className={styles.placeholderWellRecipientsIndicator}>
                        <div className={styles.placeholderPersonaPortrait} />
                        <div className={styles.placeholderPersonaText}>{recipientName}</div>
                    </div>
                )),
            [recipientNames]
        );

        return (
            <div className={styles.placeholderWellContainer}>
                {wellLabel}
                {pills}
                <input
                    onChange={onChange}
                    ref={ref as VarianceHack<React.Ref<HTMLInputElement>, typeof ref>}
                    className={styles.placeholderWellInput}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />
            </div>
        );
    }
);
